import {OutboxRenderPreferences} from "@app/core-app/app/data/profile";
import React, {useState} from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl, Input,
  Modal, ModalBody, ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay, Text,
  VStack,
} from "@chakra-ui/react";
import {event} from "../../../../services/analytics";
import {DEFAULT_RENDERER, MD_ENGINE, RENDERERS} from "@app/core-app/app/data/renderers";
import {CollapsibleSection} from "../../atoms/CollapsibleSection";

const CheckboxField = ({label, description, value, onChange, hidden}:
                         {
                           label: string,
                           description: string,
                           value: boolean,
                           onChange: (value: boolean) => void,
                           hidden?: boolean | undefined
                         }) => {

  if(hidden){
    return null
  }
  return (
    <FormControl display="flex" alignItems="center" justifyContent={"space-between"}>
      <VStack alignItems={"start"} spacing={"0"} maxW={"lg"}>
        <Text textColor={"black"} fontWeight={"medium"}>{label}</Text>
        <Text textColor={"gray.700"}>{description}</Text>
      </VStack>
      <Checkbox isChecked={value} onChange={(e) => onChange(e.target.checked)}/>
    </FormControl>)

}

const TextInputField = ({label, description, value, onChange, hidden}:
                          {
                            label: string,
                            description: string,
                            value: string,
                            onChange: (value: string) => void,
                            hidden?: boolean | undefined

                          }) => {


  if(hidden){
    return null
  }
  return (
    <FormControl display="flex" alignItems="center" justifyContent={"space-between"}>
      <VStack alignItems={"start"} spacing={"0"} maxW={"lg"}>
        <Text textColor={"black"} fontWeight={"medium"}>{label}</Text>
        <Text textColor={"gray.700"}>{description}</Text>
      </VStack>
      <Input w="xxs" type="text" textAlign={"end"}
        value={value}
        onChange={(e) => onChange(e.target.value)}/>
    </FormControl>)

}

function OutboxPreferencesComponent({renderPreferences, updatePreferences}: {
  renderPreferences: OutboxRenderPreferences;
  updatePreferences: (newPrefs: OutboxRenderPreferences) => void
}) {
  const activeEngine = renderPreferences.renderEngine || DEFAULT_RENDERER;


  const isMarkdownEngine = activeEngine === MD_ENGINE

  return (
    <VStack spacing={4} alignItems={"start"}>
      <Box>
        <Text fontSize="lg" fontWeight="bold" mb={4}>Render Engine</Text>
        <Flex direction="row" gap={4}>
          {RENDERERS.map(({key, name, description}) => {
            const isActive = activeEngine === key
            return (
              <VStack
                key={key}
                onClick={() => {
                  if (isActive) return;
                  event({action: "outbox_preferences_render_engine_changed", params: {renderEngine: key}})
                  updatePreferences({...renderPreferences, renderEngine: key})
                }}
                p={4}
                border="1px"
                borderColor={"gray.200"}
                borderRadius="lg"
                cursor="pointer"
                bg={isActive ? 'gray.50' : 'white'}
                _hover={{bg: 'gray.50'}}
                shadow="sm"
                w="50%"
                alignItems="start"
              >
                <Flex justifyContent={"space-between"} w={'full'} alignItems={"center"}>
                  <Text fontSize="md" fontWeight="medium">{name}</Text>
                  {isActive &&
                    <Text fontSize={"xs"} bgColor={"blue.100"} fontWeight="medium" px={1} py={0.5} borderRadius={"md"}>Active</Text>
                  }
                </Flex>

                <Text fontSize="sm" color="gray.500">
                  {description.map((d, index) => <Box key={`description-${index}`}>{d}</Box>)}
                </Text>
              </VStack>
            );
          })}
        </Flex>
      </Box>

      <VStack spacing={4} alignItems={"start"} w={'full'}>
        <CollapsibleSection
          isOpen={true}
          title={() => {
            return <Text fontSize="md" fontWeight="medium">General Configuration</Text>
          }}>
          <VStack spacing={4} alignItems={"start"} w={'full'}>
            <CheckboxField label={"Show page title"}
              description={"Show Notion page title at start of the email."}
              value={renderPreferences.showTitle}
              onChange={(value) => updatePreferences({...renderPreferences, showTitle: value})}
            />

            <TextInputField label={"Background color"}
              description={"Email background color (e.g. #FFFFFF)."}
              value={renderPreferences.bgColor}
              onChange={(value) => updatePreferences({...renderPreferences, bgColor: value})}
            />

            <TextInputField label={"Vertical padding"}
              description={"Vertical space between blocks in px (e.g. 2px)."}
              value={renderPreferences.verticalPadding}
              onChange={(value) => updatePreferences({...renderPreferences, verticalPadding: value})}
            />

            <TextInputField label={"Max email width"}
              description={"Maximum width for email content in px (e.g. 600px)."}
              value={renderPreferences.maxWidth}
              onChange={(value) => updatePreferences({...renderPreferences, maxWidth: value})}
            />

            <CheckboxField label={"Center Content"}
              description={"Center email content in the email. If disabled - content will be aligned to the left."}
              value={renderPreferences.centerContent}
              onChange={(value) => updatePreferences({...renderPreferences, centerContent: value})}
            />
          </VStack>
        </CollapsibleSection>

        <CollapsibleSection title={() => {
          return <Text fontSize="md" fontWeight="medium">Font Configuration</Text>
        }}>

          <TextInputField label={"Font Size"}
            description={"Text font size in px."}
            value={renderPreferences.fontSize}
            onChange={(value) => updatePreferences({...renderPreferences, fontSize: value})}
          />

          <TextInputField label={"Font Family"}
            description={"Text font family. Use only default browser fonts."}
            value={renderPreferences.fontFamily}
            onChange={(value) => updatePreferences({...renderPreferences, fontFamily: value})}
          />
          <TextInputField label={"Font Color"}
            description={"Text font color (e.g. #000000)"}
            value={renderPreferences.fontColor}
            onChange={(value) => updatePreferences({...renderPreferences, fontColor: value})}
          />

          <TextInputField label={"Line Height"}
            description={"Text Line Height"}
            value={renderPreferences.lineHeight}
            onChange={(value) => updatePreferences({...renderPreferences, lineHeight: value})}
          />

        </CollapsibleSection>


        {!isMarkdownEngine && <CollapsibleSection title={() => {
          return <Text fontSize="md" fontWeight="medium">Images Configuration</Text>
        }}>
          <VStack spacing={4} alignItems={"start"} w={'full'}>

            <CheckboxField label={"Re-host images"}
              description={"Download images from Notion before sending email. Enable this to improve\n" +
                             "                  images\n" +
                             "                  reliability in emails."}
              value={renderPreferences.downloadImages}
              onChange={(value) => updatePreferences({...renderPreferences, downloadImages: value})}
            />

            <CheckboxField label={"Center images"}
              description={"Position images at the center of the page."}
              value={renderPreferences.imagesPositioning === "center" || typeof renderPreferences.imagesPositioning === 'undefined'}
              onChange={(value) => updatePreferences({
                ...renderPreferences,
                imagesPositioning: value ? "center" : "left"
              })}
            />

            <CheckboxField label={"Full width images"}
              description={"Set images to cover the full width of the page."}
              value={renderPreferences.imagesWidth === "full_page" || typeof renderPreferences.imagesWidth === 'undefined'}
              onChange={(value) => updatePreferences({
                ...renderPreferences,
                imagesWidth: value ? "full_page" : "image_width"
              })}
            />
          </VStack>
        </CollapsibleSection>
        }

      </VStack>

    </VStack>
  );
}

function OutboxPreferencesModal({isOpen, onClose, renderPreferences, onSave}: {
  isOpen: boolean;
  onClose: () => void;
  renderPreferences: OutboxRenderPreferences;
  onSave: (newPrefs: OutboxRenderPreferences) => void
}) {
  const [localPreferences, setLocalPreferences] = useState<OutboxRenderPreferences>(renderPreferences);

  const handleSave = () => {
    onSave(localPreferences);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={"2xl"} scrollBehavior={"inside"}>
      <ModalOverlay/>
      <ModalContent minH={"75vh"} display="flex" flexDirection="column">
        <ModalHeader>Outbox Render Preferences</ModalHeader>
        <ModalCloseButton/>
        <ModalBody flex="1" overflowY="auto">
          <OutboxPreferencesComponent renderPreferences={localPreferences} updatePreferences={setLocalPreferences}/>
        </ModalBody>
        <Box p={4} bg="white" boxShadow="sm" width="full">
          <Button colorScheme="blue" onClick={handleSave} w="full">
            Save Preferences
          </Button>
        </Box>
      </ModalContent>
    </Modal>
  );
}

export function EditRenderPreferencesModal({renderPreferences, onSave, w, variant, title}: {
  w?: string | undefined | null;
  title?: string | undefined | null
  variant?: string | undefined | null
  renderPreferences: OutboxRenderPreferences;
  onSave: (newPrefs: OutboxRenderPreferences) => void
}) {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenModal = () => {
    event({action: "open_edit_render_preferences_modal", params: {}})
    setIsOpen(true);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  return (
    <>
      <Button w={w} size="sm" colorScheme="blue" variant={variant ?? "outline"} onClick={handleOpenModal}>
        <span>{title ?? 'Update Outbox Preferences'}</span>
      </Button>
      <OutboxPreferencesModal isOpen={isOpen} onClose={handleCloseModal} renderPreferences={renderPreferences}
        onSave={onSave}/>
    </>
  );
}
